import { useState } from "react";
import { toast } from "react-toastify";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Stack, Typography,
} from "@mui/material";

import api from "../api";

const DTYPES = ["auto", "float16", "bfloat16", "float32"];
const QUANTS = ["", "awq", "gptq", "fp8", "bitsandbytes"];

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

// Create OR edit a vLLM model. `model=null` → create; otherwise edit (name fixed).
export default function VllmModelModal({ model, onClose }) {
  const editing = !!model;
  const [f, setF] = useState({
    name: model?.name || "",
    model_id: model?.model_id || "",
    description: model?.description || "",
    tensor_parallel_size: model?.tensor_parallel_size || 1,
    eligible_gpus: (model?.eligible_gpus || []).join(", "),
    max_model_len: model?.max_model_len ?? "",
    dtype: model?.dtype || "auto",
    quantization: model?.quantization || "",
    gpu_memory_utilization: model?.gpu_memory_utilization ?? "",
    vram_estimate_mb: model?.vram_estimate_mb ?? "",
    extra_args: (model?.extra_args || []).join(" "),
  });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const payload = () => ({
    model_id: f.model_id.trim(),
    description: f.description,
    tensor_parallel_size: Number(f.tensor_parallel_size) || 1,
    eligible_gpus: f.eligible_gpus.split(/[,\s]+/).filter(Boolean).map(Number),
    max_model_len: numOrNull(f.max_model_len),
    dtype: f.dtype === "auto" ? null : f.dtype,
    quantization: f.quantization || null,
    gpu_memory_utilization: numOrNull(f.gpu_memory_utilization),
    vram_estimate_mb: numOrNull(f.vram_estimate_mb),
    extra_args: f.extra_args.split(/\s+/).filter(Boolean),
  });

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/api/vllm-models/${model.name}`, payload());
      else await api.post("/api/vllm-models", { name: f.name.trim(), ...payload() });
      toast.success(editing ? "Model saved" : "Model created");
      onClose(true);
    } catch {
      /* api wrapper toasts */
    } finally {
      setBusy(false);
    }
  };

  const canSave = editing || (f.name.trim() && f.model_id.trim());

  return (
    <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${model.name}` : "New vLLM model"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" label="Name" placeholder="my-llm" value={f.name}
              onChange={up("name")} disabled={editing} sx={{ flex: 1 }}
              helperText="Slug clients send as the OpenAI model id" />
            <TextField size="small" label="Tensor parallel (GPUs)" type="number"
              value={f.tensor_parallel_size} onChange={up("tensor_parallel_size")} sx={{ width: 160 }} />
          </Stack>
          <TextField size="small" label="HuggingFace model id" placeholder="ibm-granite/granite-docling-258M"
            value={f.model_id} onChange={up("model_id")} />
          <TextField size="small" label="Description" value={f.description} onChange={up("description")} />
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" label="dtype" select value={f.dtype} onChange={up("dtype")} sx={{ flex: 1 }}>
              {DTYPES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Quantization" select value={f.quantization}
              onChange={up("quantization")} sx={{ flex: 1 }}>
              {QUANTS.map((q) => <MenuItem key={q || "none"} value={q}>{q || "none"}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" label="Max model len" type="number" value={f.max_model_len}
              onChange={up("max_model_len")} sx={{ flex: 1 }} />
            <TextField size="small" label="GPU mem %" type="number" value={f.gpu_memory_utilization}
              onChange={up("gpu_memory_utilization")} sx={{ flex: 1 }} placeholder="90" />
            <TextField size="small" label="VRAM est. (MB)" type="number" value={f.vram_estimate_mb}
              onChange={up("vram_estimate_mb")} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" label="Eligible GPUs (global indices, comma-sep; blank = any)"
            placeholder="0, 1" value={f.eligible_gpus} onChange={up("eligible_gpus")} />
          <TextField size="small" label="Extra vllm args (space-separated)"
            placeholder="--enforce-eager --trust-remote-code" value={f.extra_args} onChange={up("extra_args")} />
          <Typography variant="caption" color="text.secondary">
            Starting a model builds the shared vLLM environment (once) and loads it onto a GPU.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="inherit">Cancel</Button>
        <Button onClick={save} disabled={busy || !canSave} variant="contained">
          {editing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
